/**
 * Client-side HEIC to PNG/JPEG conversion utility
 * Uses lazy-loaded heic-to library to avoid bundle bloat
 */

/**
 * Check if file is HEIC (synchronous, zero overhead)
 * Fast check using extension and MIME type only
 */
export function isHeicFile(file: File): boolean {
  const isHeic = file.type === "image/heic" || file.type === "image/heif";
  const hasHeicExtension = file.name.toLowerCase().endsWith(".heic") || file.name.toLowerCase().endsWith(".heif");
  return isHeic || hasHeicExtension;
}

/**
 * Format error messages for user display
 */
function formatConversionError(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  if (error && typeof error === "object") {
    const errorObj = error as any;
    if (errorObj.message) return errorObj.message;
    if (errorObj.error) return String(errorObj.error);
  }
  return "Unknown error occurred";
}

/**
 * Convert HEIC to JPEG (with PNG fallback)
 * LAZY LOADS heic-to only when called
 * @param file - HEIC file to convert
 * @returns Converted file as JPEG or PNG
 */
export async function convertHeic(file: File): Promise<File> {
  // Dynamic import - only loads when HEIC conversion needed
  const { heicTo } = await import("heic-to");

  try {
    // Try JPEG first (smaller file size, good for photos)
    console.log(`[heicConversion] Converting HEIC to JPEG: ${file.name}`);

    const blob = await heicTo({
      blob: file,
      type: "image/jpeg",
      quality: 0.9,
    });

    const baseName = file.name.replace(/\.(heic|heif)$/i, "");
    const convertedFile = new File([blob], `${baseName}.jpg`, {
      type: "image/jpeg",
      lastModified: Date.now(),
    });

    console.log(
      `[heicConversion] Converted ${file.name} (${Math.round(file.size / 1024)}KB) to JPEG (${Math.round(
        convertedFile.size / 1024
      )}KB)`
    );

    return convertedFile;
  } catch (jpegError) {
    // Fallback to PNG if JPEG fails
    console.warn("[heicConversion] JPEG failed, trying PNG:", jpegError);

    try {
      const blob = await heicTo({
        blob: file,
        type: "image/png",
        quality: 0.95,
      });

      const baseName = file.name.replace(/\.(heic|heif)$/i, "");
      const convertedFile = new File([blob], `${baseName}.png`, {
        type: "image/png",
        lastModified: Date.now(),
      });

      console.log(
        `[heicConversion] Converted to PNG: ${convertedFile.name} (${Math.round(convertedFile.size / 1024)}KB)`
      );

      return convertedFile;
    } catch (pngError) {
      const errorMessage = `Failed to convert HEIC file: ${formatConversionError(pngError)}`;

      console.error("[heicConversion] Conversion failed:", {
        file: file.name,
        size: file.size,
        type: file.type,
        error: pngError,
      });

      throw new Error(errorMessage);
    }
  }
}
